import { removeHtmlTag } from '@exam-management/common/utils';
import {
  CommentEntityType,
  useGetQuestionCommentsByEntityAndTaskIdQuery,
  useGetQuestionVerdictsByEntityAndTaskIdQuery,
  Verdict,
  VerdictEntityType,
} from '@exam-management/graphql';
import { QuestionReviewReviewerCard } from '@exam-management/new-question-review-task-details';
import { ReviewResultBadge } from '@exam-management/task-management';
import { ReadonlyField, Skeleton } from '@shared/components';

import { QuestionReviewContentPreviewer } from '../../components';

interface ReviewResultCardBySubtaskProps {
  subtaskName: string;
  assignee: string;
  taskId: string;
  questionId: string;
  subTaskId: string;
  taskCode: string;
  assigneeId: string;
  dueDate: Date | null;
  order: number;
}

const COLOR_MAPPER = {
  approved: 'success',
  rejected: 'error',
} as const;

const REVIEW_RESULT_MAPPER = {
  [Verdict.Approved]: 'approved',
  [Verdict.Rejected]: 'rejected',
} as const;

export default function ReviewResultCardBySubtask(props: Readonly<ReviewResultCardBySubtaskProps>) {
  const { assignee, questionId, assigneeId, subTaskId, order, taskCode, dueDate } = props;

  const { data: questionVerdictsResult, loading: isGettingQuestionVerdicts } =
    useGetQuestionVerdictsByEntityAndTaskIdQuery({
      variables: {
        input: {
          entityId: questionId,
          taskId: subTaskId,
          entityType: VerdictEntityType.Question,
        },
      },
    });

  const { data: questionCommentResult, loading: isGettingQuestionComment } =
    useGetQuestionCommentsByEntityAndTaskIdQuery({
      variables: {
        input: {
          entityId: questionId,
          taskId: subTaskId,
          entityType: CommentEntityType.Question,
        },
      },
    });

  const verdictResult = questionVerdictsResult?.qctGetQuestionVerdictsByEntityAndTaskId?.find(
    (verdict) => verdict.userId === assigneeId,
  );
  const commentResult = questionCommentResult?.qctGetQuestionCommentsByEntityAndTaskId.find(
    (comment) => comment.userId === assigneeId,
  );

  const reviewResult = verdictResult?.verdict
    ? REVIEW_RESULT_MAPPER[verdictResult?.verdict as keyof typeof REVIEW_RESULT_MAPPER]
    : null;

  const color = reviewResult ? COLOR_MAPPER[reviewResult] : 'grey';

  const isLoading = isGettingQuestionVerdicts || isGettingQuestionComment;

  const commentContent =
    commentResult?.content && removeHtmlTag(commentResult.content, { customTag: 'p' })
      ? commentResult.content
      : '-';

  if (isLoading) {
    return <Skeleton variant='rounded' height={268} />;
  }

  return (
    <QuestionReviewReviewerCard
      key={subTaskId}
      order={order}
      name={assignee}
      taskCode={taskCode}
      color={color ?? 'grey'}
      dueDate={dueDate}
      infoEndAdornment={reviewResult ? <ReviewResultBadge result={reviewResult} /> : null}
    >
      <ReadonlyField
        label='ความเห็นเพิ่มเติม'
        content={
          <QuestionReviewContentPreviewer
            content={commentContent}
            questionId={questionId}
            taskId={subTaskId}
          />
        }
      />
    </QuestionReviewReviewerCard>
  );
}
